import type { Portfolio } from 'venn-api';
import { sum } from 'lodash';
import { mergePortfolios, recursiveGetFunds } from 'venn-utils';
import { lotsToInputs } from '../../../../allocation-panel/allocation-search/tax-info/types';
import { hasInconsistentLotPricing } from '../../../../allocation-panel/allocation-search/tax-info/utils';

type TaxImpactRowType = 'TRANSACTION' | 'UNEXECUTED' | 'UNFILLED' | 'MISSING' | 'TOTAL';

export type TaxImpactRow = {
  label: string | undefined;
  path: string[];
  isStrategy: boolean;
  purchaseDate: string | undefined;
  holdingPeriod: string | undefined;
  marketValue: number | undefined;
  costBasis: number | undefined;
  startingQuantity: number | undefined;
  transactedQuantity: number | undefined;
  endingQuantity: number | undefined;
  gainLoss: number | undefined;
  estimatedTax: number | undefined;
  taxStatus: string | undefined;
  rowType: TaxImpactRowType;
  hasInconsistentPricing: boolean | undefined;
};

export type ProcessedLot = Omit<TaxImpactRow, 'path' | 'isStrategy' | 'hasInconsistentPricing'> & {
  investmentId: string;
};

const rollUpNumericValues = (rows: (TaxImpactRow | ProcessedLot)[]) => ({
  marketValue: sum(rows.map((r) => r.marketValue ?? 0)),
  costBasis: sum(rows.map((r) => r.costBasis ?? 0)),
  startingQuantity: sum(rows.map((r) => r.startingQuantity)),
  transactedQuantity: sum(rows.map((r) => r.transactedQuantity)),
  endingQuantity: sum(rows.map((r) => r.endingQuantity)),
  gainLoss: sum(rows.map((r) => r.gainLoss)),
  estimatedTax: sum(rows.map((r) => r.estimatedTax)),
});

const buildInvestmentRows = (
  node: Portfolio,
  parentPath: string[],
  lots: ProcessedLot[],
  nameIndex: number,
): TaxImpactRow[] => {
  const displayName = node.name ?? '';
  const investmentPath = [...parentPath, `${displayName}(${nameIndex})`];
  const hasInconsistentPricing = hasInconsistentLotPricing({ lots: lotsToInputs(node.lots ?? []) });

  if (lots.length <= 1) {
    const lot = lots[0]!;
    return [{ ...lot, path: investmentPath, label: displayName, isStrategy: false, hasInconsistentPricing }];
  }

  return [
    {
      path: investmentPath,
      label: displayName,
      rowType: 'TOTAL',
      isStrategy: false,
      purchaseDate: undefined,
      holdingPeriod: undefined,
      taxStatus: undefined,
      hasInconsistentPricing,
      ...rollUpNumericValues(lots),
    },
    ...lots.map(
      (lot, idx): TaxImpactRow => ({
        ...lot,
        path: [...investmentPath, `lot-${idx}`],
        label: displayName,
        isStrategy: false,
        hasInconsistentPricing: undefined,
      }),
    ),
  ];
};

const collectLotsUnderStrategy = (
  children: Portfolio[],
  lotsByInvestment: Record<string, ProcessedLot[]>,
): ProcessedLot[] => {
  const lots: ProcessedLot[] = [];
  for (const child of children) {
    if (child.fund) {
      const fundLots = lotsByInvestment[child.fund.id];
      if (fundLots?.length) {
        lots.push(...fundLots);
      }
    } else {
      lots.push(...collectLotsUnderStrategy(child.children ?? [], lotsByInvestment));
    }
  }
  return lots;
};

export const buildTaxImpactTreeRows = ({
  portfolioChildren,
  parentPath,
  depth,
  lotsByInvestment,
  selectedLevels,
  placedInvestmentIds,
  usedNames,
}: {
  portfolioChildren: Portfolio[];
  parentPath: string[];
  depth: number;
  lotsByInvestment: Record<string, ProcessedLot[]>;
  selectedLevels: (number | string)[];
  placedInvestmentIds: Set<string>;
  usedNames: Record<string, number>;
}): TaxImpactRow[] => {
  const rows: TaxImpactRow[] = [];

  for (const child of portfolioChildren) {
    if (child.fund) {
      const lots = lotsByInvestment[child.fund.id];
      if (!lots?.length) continue;

      placedInvestmentIds.add(child.fund.id);

      if (!selectedLevels.includes('investments')) continue;

      const nameCount = usedNames[child.name] ?? 0;
      usedNames[child.name] = nameCount + 1;
      rows.push(...buildInvestmentRows(child, parentPath, lots, nameCount));
    } else {
      const includeLevel = selectedLevels.includes(depth);

      if (includeLevel) {
        const nameCount = usedNames[child.name] ?? 0;
        usedNames[child.name] = nameCount + 1;
        const childPath = [...parentPath, `${child.name}(${nameCount})`];

        const childRows = buildTaxImpactTreeRows({
          portfolioChildren: child.children ?? [],
          parentPath: childPath,
          depth: depth + 1,
          lotsByInvestment,
          selectedLevels,
          placedInvestmentIds,
          usedNames: {},
        });

        const lotsUnderStrategy = collectLotsUnderStrategy(child.children ?? [], lotsByInvestment);
        if (lotsUnderStrategy.length > 0) {
          const directChildRows = childRows.filter((r) => r.path.length === childPath.length + 1);
          rows.push(
            {
              path: childPath,
              label: child.name,
              rowType: 'TOTAL',
              isStrategy: true,
              hasInconsistentPricing: undefined,
              purchaseDate: undefined,
              holdingPeriod: undefined,
              taxStatus: undefined,
              ...(directChildRows.length > 0
                ? rollUpNumericValues(directChildRows)
                : rollUpNumericValues(lotsUnderStrategy)),
            },
            ...childRows,
          );
        }
      } else {
        rows.push(
          ...buildTaxImpactTreeRows({
            portfolioChildren: child.children ?? [],
            parentPath,
            depth: depth + 1,
            lotsByInvestment,
            selectedLevels,
            placedInvestmentIds,
            usedNames,
          }),
        );
      }
    }
  }

  return rows;
};

const removeFundNodes = ({ children, fundIds }: { children: Portfolio[]; fundIds: Set<string> }): Portfolio[] =>
  children
    .filter((child) => !child.fund || !fundIds.has(child.fund.id))
    .map((child) => ({ ...child, children: removeFundNodes({ children: child.children ?? [], fundIds }) }));

// Lots carry only an investment id, so shared holdings render once: where the current portfolio holds them.
export const mergeCurrentAndTargetPortfolios = ({
  current,
  target,
}: {
  current: Portfolio | undefined;
  target: Portfolio | undefined;
}): Portfolio[] => {
  const currentFundIds = new Set(recursiveGetFunds(current).map((fund) => fund.id));
  const targetOnlyHoldings = target && {
    ...target,
    children: removeFundNodes({ children: target.children ?? [], fundIds: currentFundIds }),
  };
  return mergePortfolios([current, targetOnlyHoldings]);
};
